import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { MarqueeBar } from "../components/MarqueeBar";
import { ProductCard } from "../components/ProductCard";
import { MediaEmbed } from "../components/MediaEmbed";

const HEROES = {
  punk: "https://images.unsplash.com/photo-1546708770-589dab7b22c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  fitness: "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  sports: "https://images.unsplash.com/photo-1700319021396-95aec8e168ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  street: "https://images.unsplash.com/photo-1532332248682-206cc786359f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  print: "https://images.unsplash.com/photo-1589835783054-90aa9fc32915?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};
const HERO_LABELS = { punk: "Punk", sports: "Sports", fitness: "Fitness", street: "Street", print: "Prints" };

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 8)));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="border-b-2 border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-0">
          <h1 className="font-anton text-[15vw] md:text-[11vw] leading-[0.82] uppercase tracking-tighter">
            Wear It.<br />
            <span className="text-primary">Print It.</span><br />
            <span className="text-outline">Own It.</span>
          </h1>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-8 mb-10">
            <p className="font-mono text-sm md:text-base max-w-md text-muted-foreground">
              From the mosh pit to the squat rack. Buy the tee, or pick a print and we'll mail it straight to your door.
            </p>
            <Link to="/shop" data-testid="hero-shop-btn" className="inline-flex items-center gap-3 bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm px-8 py-4 border-2 border-border hover:bg-secondary hover:border-secondary hover:text-white transition-colors">
              Shop The Drop <ArrowRight size={18} />
            </Link>
          </div>
          {settings.hero_video && (
            <div className="mb-10 border-2 border-border overflow-hidden aspect-video max-h-[520px]" data-testid="hero-video">
              <MediaEmbed url={settings.hero_video} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
        {/* category grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 border-t-2 border-border">
          {["punk", "sports", "fitness", "street", "print"].map((cat, i) => (
            <Link
              key={cat}
              to={cat === "print" ? "/shop?type=print" : `/shop?category=${cat}`}
              data-testid={`hero-category-${cat}`}
              className={`group relative h-72 md:h-96 overflow-hidden ${i < 4 ? "md:border-r-2 border-border" : ""} border-b-2 md:border-b-0`}
            >
              <img src={HEROES[cat]} alt={cat} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
              <div className="absolute bottom-0 left-0 p-6 flex items-center gap-3">
                <span className="font-anton text-4xl md:text-5xl uppercase text-white tracking-tighter">{HERO_LABELS[cat]}</span>
                <ArrowRight className="text-white opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <MarqueeBar />

      {settings.promo_video && (
        <section className="bg-black border-b-2 border-border" data-testid="promo-section">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">In Motion</span>
              <h2 className="font-anton text-5xl md:text-7xl uppercase tracking-tighter mt-3 leading-none text-white">{settings.promo_title || "Watch The Drop"}</h2>
              <p className="font-mono text-sm text-white/60 mt-5 max-w-md">{settings.promo_text || "See the gear in motion."}</p>
              <Link to="/shop" className="inline-block mt-8 bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm px-8 py-4 border-2 border-primary hover:bg-secondary hover:text-white hover:border-secondary transition-colors">
                Shop The Look
              </Link>
            </div>
            <div className="border-2 border-white overflow-hidden aspect-video" data-testid="promo-video">
              <MediaEmbed url={settings.promo_video} className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10 border-b-2 border-border pb-4">
          <h2 className="font-anton text-4xl md:text-6xl uppercase tracking-tighter">Featured Gear</h2>
          <Link to="/shop" className="font-outfit font-bold uppercase text-sm hover:text-primary transition-colors hidden md:flex items-center gap-2">
            View All <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black border border-border">
          {featured.map((p) => (
            <div key={p.id} className="bg-card">
              <ProductCard product={p} />
            </div>
          ))}
        </div>
      </section>

      {/* PRINT IT BANNER */}
      <section className="bg-black text-white py-16 md:py-24 border-y-2 border-border">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Don't want the tee?</span>
            <h2 className="font-anton text-5xl md:text-7xl uppercase tracking-tighter mt-3 leading-none">Just Pick A Print</h2>
            <p className="font-mono text-sm text-white/60 mt-5 max-w-md">
              Choose any design from our catalog. We print it on heavyweight stock and mail it straight to your door — no tee required.
            </p>
            <Link to="/shop?type=print" data-testid="banner-prints-btn" className="inline-block mt-8 bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm px-8 py-4 border-2 border-primary hover:bg-secondary hover:text-white hover:border-secondary transition-colors">
              Browse Prints
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1589835783054-90aa9fc32915?auto=format&fit=crop&w=500&q=80" alt="print" className="border-2 border-white aspect-[3/4] object-cover w-full" />
            <img src="https://images.unsplash.com/photo-1604161546853-1a097fbc30fb?auto=format&fit=crop&w=500&q=80" alt="print" className="border-2 border-white aspect-[3/4] object-cover w-full mt-8" />
          </div>
        </div>
      </section>
    </div>
  );
}
